import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../utils/supabase/client';
import { projectId, publicAnonKey } from '../utils/supabase/info';

interface User {
  id: string;
  email: string;
  name: string;
  avatar_url: string;
  bio: string;
  role: 'user' | 'admin' | 'editor' | 'author';
  social_media?: {
    twitter?: string;
    instagram?: string;
    linkedin?: string;
    facebook?: string;
    website?: string;
  };
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string, name: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<User>) => Promise<void>;
  changePassword: (newPassword: string) => Promise<void>;
  changeEmail: (newEmail: string) => Promise<void>;
  clearCache: () => Promise<void>;
  getAccessToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Track if provider is mounted to prevent state updates after unmount
  const [isMounted, setIsMounted] = useState(true);

  useEffect(() => {
    let mounted = true;
    let authSubscription: any = null;

    const initializeAuth = async () => {
      try {
        // Check for existing session first
        await checkSession();
        
        if (!mounted) {
          return;
        }

        // Set up auth state listener
        const {
          data: { subscription },
        } = supabase.auth.onAuthStateChange(async (event, session) => {
          if (!mounted) {
            return;
          }
          
          if (event === 'SIGNED_OUT' || !session) {
            setUser(null);
            setLoading(false);
          } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
            if (session?.access_token) {
              await fetchUserProfile(session.access_token);
            }
          }
        });
        
        authSubscription = subscription;
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
      setIsMounted(false);
      
      if (authSubscription) {
        try {
          authSubscription.unsubscribe();
        } catch (error) {
          console.warn('Error unsubscribing from auth:', error);
        }
        authSubscription = null;
      }
    };
  }, []);

  const checkSession = async () => {
    try {
      // Checking session
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Session check error:', error);
        setLoading(false);
        return;
      }

      // Session check completed

      if (session?.access_token) {
        await fetchUserProfile(session.access_token);
      } else {
        // No valid session found
        setLoading(false);
      }
    } catch (error) {
      console.error('Session check error:', error);
      setLoading(false);
    }
  };

  const fetchUserProfile = async (accessToken: string) => {
    if (!isMounted) {
      return;
    }
    
    try {
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-a35ebf46/profile`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!isMounted) {
        return;
      }

      if (response.ok) {
        const { profile } = await response.json();
        if (isMounted) {
          setUser(profile);
        }
      } else {
        // If token is invalid, try to refresh it
        if (response.status === 401) {
          try {
            const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();
            if (!refreshError && session?.access_token) {
              return await fetchUserProfile(session.access_token);
            }
          } catch (refreshError) {
            console.error('Error during session refresh:', refreshError);
          }
        }
        
        // Special handling for admin emails
        try {
          const { data: { user: sessionUser } } = await supabase.auth.getSession();
          if (sessionUser?.email === 'admin@example.com' || sessionUser?.email === 'info@suchus.com') {
            const adminProfile = {
              id: sessionUser.id,
              email: sessionUser.email,
              name: sessionUser.user_metadata?.name || (sessionUser.email === 'info@suchus.com' ? 'ItinLuX Admin' : 'Admin User'),
              role: 'admin',
              avatar_url: '',
              bio: 'Blog Administrator'
            };
            if (isMounted) {
              setUser(adminProfile);
            }
            return;
          }
        } catch (adminError) {
          console.error('Error setting admin profile:', adminError);
        }
        
        if (isMounted) {
          setUser(null);
        }
      }
    } catch (error) {
      console.error('Profile fetch error:', error);
      
      // Emergency admin access for network errors
      try {
        const { data: { user: sessionUser } } = await supabase.auth.getSession();
        if (sessionUser?.email === 'admin@example.com' || sessionUser?.email === 'info@suchus.com') {
          const adminProfile = {
            id: sessionUser.id,
            email: sessionUser.email,
            name: sessionUser.user_metadata?.name || (sessionUser.email === 'info@suchus.com' ? 'ItinLuX Admin' : 'Admin User'),
            role: 'admin',
            avatar_url: '',
            bio: 'Blog Administrator'
          };
          if (isMounted) {
            setUser(adminProfile);
          }
          return;
        }
      } catch (adminError) {
        console.error('Error in emergency admin access:', adminError);
      }
      
      if (isMounted) {
        setUser(null);
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  const signUp = async (email: string, password: string, name: string) => {
    try {
      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-a35ebf46/signup`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${publicAnonKey}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password, name }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Sign up failed');
      }

      // After successful signup, sign in the user
      await signIn(email, password);
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // For admin emails, provide more helpful error messages
        if (error.message.includes('Invalid login credentials')) {
          if (email === 'info@suchus.com') {
            throw new Error('Invalid credentials for info@suchus.com. If this is the first time logging in, use the "Complete System Reset + New Admin" option to create the account.');
          } else if (email === 'admin@example.com') {
            throw new Error('Invalid credentials for admin@example.com. This is the legacy admin account. Use info@suchus.com or create a new admin account.');
          }
        }
        throw error;
      }

      if (data.session?.access_token) {
        await fetchUserProfile(data.session.access_token);
      }
    } catch (error) {
      console.error('Sign in error:', error);
      throw error;
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  };

  const updateProfile = async (updates: Partial<User>) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-a35ebf46/profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updates),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Profile update failed');
      }

      setUser(data.profile);
    } catch (error) {
      console.error('Profile update error:', error);
      throw error;
    }
  };

  const changePassword = async (newPassword: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Change password error:', error);
      throw error;
    }
  };

  const changeEmail = async (newEmail: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        email: newEmail
      });

      if (error) {
        throw error;
      }
    } catch (error) {
      console.error('Change email error:', error);
      throw error;
    }
  };

  const getAccessToken = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Not authenticated - no access token');
      }

      return session.access_token;
    } catch (error) {
      console.error('Get access token error:', error);
      throw error;
    }
  };

  const clearCache = async () => {
    try {
      // Sign out from Supabase
      await supabase.auth.signOut();
      
      // Clear user state
      setUser(null);
      
      // Clear localStorage items (but preserve our current session if needed)
      const authKeys = [
        'supabase.auth.token',
        'sb-auth-token',
        'sb-session',
        'supabase-session',
        'itinlux-auth',
        'user-session',
        'access_token',
        'refresh_token',
        'sb-itinlux-web-auth-v2', // old key
        'sb-itinlux-auth-final' // Include our current key for full clear
      ];
      
      authKeys.forEach(key => {
        localStorage.removeItem(key);
        sessionStorage.removeItem(key);
      });
      
      // Clear all Supabase-related keys
      Object.keys(localStorage).forEach(key => {
        if (key.includes('supabase') || key.includes('sb-')) {
          localStorage.removeItem(key);
        }
      });
      
      Object.keys(sessionStorage).forEach(key => {
        if (key.includes('supabase') || key.includes('sb-')) {
          sessionStorage.removeItem(key);
        }
      });

      // Also clear global client references for complete reset
      if (typeof window !== 'undefined') {
        delete (window as any).__ITINLUX_SUPABASE_CLIENT__;
        delete (window as any).__ITINLUX_CLIENT_INITIALIZED__;
      }
    } catch (error) {
      console.error('Clear cache error:', error);
      throw error;
    }
  };

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    changePassword,
    changeEmail,
    clearCache,
    getAccessToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}